// Libraries
import { Dispatch, SetStateAction, useEffect, useState } from 'react';

/**
 * Hook to manage a state value with debouncing.
 *
 * This hook is useful for delaying the processing of state updates until after the user stops making frequent changes for a specified amount of time (delay).
 *
 * @type Type - The type of the state value being managed.
 *
 * @param initialValue - The initial value for the state.
 * @param delay - The debouncing delay in milliseconds. Default value is `300`.
 * @param onChange - Optional callback to be invoked whenever the debounced value changes.
 *
 * @returns A tuple:
 * @returns {Type} value - The internal (immediate) state value.
 * @returns {Dispatch<SetStateAction<Type>>} setValue - A setter function to set the internal state value.
 * @returns {Type} debouncedValue - Optional. The debounced state value.
 *
 * @example
 * ```tsx
 * const [searchTerm, setSearchTerm, debouncedSearchTerm] = useDebouncedState<string>('', 500);
 * ```
 *
 * @remarks
 * - When the `internalValue` changes, it doesn't immediately reflect on the `debouncedValue`.
 *   Instead, it waits for the specified `delay` before updating the `debouncedValue`.
 * - If the `onChange` callback is provided, it will be invoked every time the `debouncedValue` changes.
 */
export const useDebouncedState = <Type>(initialValue: Type, delay = 300, onChange?: (value: Type) => void) => {
  const [internalValue, setInternalValue] = useState<Type>(initialValue);
  const [debouncedValue, setDebouncedValue] = useState<Type>(initialValue);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(internalValue);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [internalValue, delay]);

  useEffect(() => {
    setInternalValue(initialValue);
  }, [initialValue]);

  useEffect(() => {
    if (onChange) {
      onChange(debouncedValue);
    }
    // Note: We don't need to track the onChange instance change
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedValue]);

  return [internalValue, setInternalValue, debouncedValue] as [Type, Dispatch<SetStateAction<Type>>, Type];
};
